import React, { useState } from 'react';
import { ClusterOutlined, LockOutlined } from '@ant-design/icons';
import { applyCurrentToHub } from '../../api/api';
import type { Card } from '../../types/card';
import { isPendingApplied } from '../../types/hubChanges';
import { FONT_SIZE, FONT_WEIGHT, RADIUS, SPACING, type ThemeTokens } from '../../constants/constants';
import { storePriceLabel } from '../../constants/stores';
import { useStorage } from '../../contexts/StorageContext';
import { appMessage as message } from '../../utils/appMessage';
import { appModal } from '../../utils/appModal';
import { formatPrice } from '../../utils/format';
import { AppButton } from './AppButton';

// 사람이 정한 가격 보호(백엔드 PR #77 Q2, 감사 card-controltower#538 · #558) — '유지(✗)' · ② 적용가 · 판매가 저장 때의 PriceHub 값이
// 그대로면 가격 받기가 덮지 않는다. PriceHub 가격이 바뀌면 풀려 다시 검토.

export const PRICE_HELD_TEXT = '유지 중 — PriceHub 가격이 바뀌면 다시 검토';

/** '유지 중' 표시 — 글은 짧게, 전체 뜻은 title · aria-label. */
export const PriceHeldTag: React.FC<{ t: ThemeTokens; testId?: string }> = ({ t, testId }) => (
  <span
    data-testid={testId ?? 'price-held'}
    title={PRICE_HELD_TEXT}
    aria-label={PRICE_HELD_TEXT}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.semibold,
      color: t.textSecondary, background: t.bgRaised, border: `1px solid ${t.border}`, borderRadius: RADIUS.sm,
      padding: `0 ${SPACING.xxs}px`, whiteSpace: 'nowrap', verticalAlign: 'middle',
    }}
  >
    <LockOutlined aria-hidden /> 유지 중
  </span>
);

/**
 * 보호 중인데 PriceHub 가격(hubPrice)이 네이버 가격(기준가)과 다른 카드 — 사람이 네이버 가격을 지켰으니 PriceHub 에 그 값을 매장 가격으로
 * 요청해야 둘이 맞는다(#558 M2). 보호 때문에 상승 · 하락이 아니게 되어 하락 검토 · 매장 가격 요청 목록에서 사라지던 카드.
 * 백엔드 조건은 따로(지후) — 프런트는 priceHeld + 가격 차이로만 본다.
 */
export const needsHubRequest = (c: Pick<Card, 'priceHeld' | 'hubPrice' | 'currentPrice'>): boolean =>
  c.priceHeld === true && c.hubPrice != null && c.currentPrice != null && c.currentPrice > 0 && c.hubPrice !== c.currentPrice;

/**
 * '매장 가격 요청' — 이 카드의 네이버 가격을 <매장> 가격으로 삼아 달라고 PriceHub 에 요청(apply-current, 매장 가격 요청 화면과 같은 API).
 * 확인 창을 거치고, 보낸 뒤에는 목록을 다시 받을 때까지 '요청함' 으로 잠근다(같은 요청 두 번 방지).
 */
export const HubRequestButton: React.FC<{ card: Card; t: ThemeTokens; size?: 'sm' | 'md' | 'lg' }> = ({ card, t, size = 'sm' }) => {
  const { isAdmin, store, superStore } = useStorage();
  const scope = store ?? superStore ?? null;
  const name = storePriceLabel(scope);
  const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle');
  if (!isAdmin) return null;

  const send = async () => {
    setState('sending');
    try {
      const r = await applyCurrentToHub([card.id], { store: scope });
      const failed = r.failed.find((f) => f.cardId === card.id);
      if (failed) {
        message.warning({ key: `hub-req-${card.id}`, content: `${card.productName} — 요청하지 못했습니다: ${failed.reason}`, duration: 8 });
        setState('idle');
        return;
      }
      const pending = r.applied.some(isPendingApplied);
      message.success({
        key: `hub-req-${card.id}`,
        content: pending
          ? `${card.productName} — PriceHub 에 요청했습니다(확정 대기). PriceHub 관리자가 확정하면 ${name}이 바뀝니다.`
          : `${card.productName} — ${name}을 네이버 가격으로 바꿨습니다.`,
        duration: 5,
      });
      setState('sent');
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      message.error({ key: `hub-req-${card.id}`, content: msg || '매장 가격 요청 중 오류가 발생했습니다 — 매장 가격 요청 화면에서 확인하세요.', duration: 8 });
      setState('idle');
    }
  };

  const ask = () => appModal.confirm({
    title: `${name}으로 요청`,
    content: `${card.productName} — ${name}을 네이버 가격 ${formatPrice(card.currentPrice as number)}으로 바꿔 달라고 PriceHub 에 요청합니다`
      + `(지금 PriceHub ${formatPrice(card.hubPrice as number)}, PriceHub 관리자 확인 뒤 반영, 네이버 판매가는 그대로).`,
    okText: 'PriceHub 에 요청',
    cancelText: '취소',
    onOk: () => { void send(); },
  });

  return (
    <AppButton
      size={size} variant="default"
      icon={<ClusterOutlined aria-hidden />}
      loading={state === 'sending'}
      disabled={state === 'sent'}
      disabledReason={state === 'sent' ? '요청을 보냈습니다 — 매장 가격 요청 화면의 확정 대기에서 확인하세요' : undefined}
      title={`네이버 가격 ${formatPrice(card.currentPrice as number)} → ${name} 요청(PriceHub ${formatPrice(card.hubPrice as number)})`}
      style={{ borderColor: t.lineAccent, color: t.accent }}
      onClick={ask}
    >
      {state === 'sent' ? '요청함' : '매장 가격 요청'}
    </AppButton>
  );
};
